/**
 * @name ModalCadastroDados
 * @author Gabriel M. Guimarães | gabrielmg7
 * @description Modal para completar dados cadastrais necessários para PIX
 * @package components/pages/gestao-financeira/creditos/components
 */

import { Close as CloseIcon, Save as SaveIcon } from '@mui/icons-material';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';

interface DadosEndereco {
     cep: string;
     logradouro: string;
     bairro: string;
     cidade: string;
     uf: string;
     numero?: string;
     complemento?: string;
}

interface DadosTelefone {
     ddd: string;
     numero: string;
     tipo: 'principal' | 'secundario';
}

interface DadosCompletos {
     endereco?: DadosEndereco;
     telefone?: DadosTelefone;
}

interface ModalCadastroDadosProps {
     open: boolean;
     onClose: () => void;
     onSave: (dados: DadosCompletos) => Promise<string | null | undefined>;
     camposFaltantes: string[];
     loading?: boolean;
}

const ESTADOS_BRASIL = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];

const ModalCadastroDados: React.FC<ModalCadastroDadosProps> = ({ open, onClose, onSave, camposFaltantes, loading = false }) => {
     // Estados do formulário
     const [endereco, setEndereco] = useState<DadosEndereco>({
          cep: '',
          logradouro: '',
          bairro: '',
          cidade: '',
          uf: '',
          numero: '',
          complemento: '',
     });

     const [telefone, setTelefone] = useState<DadosTelefone>({
          ddd: '',
          numero: '',
          tipo: 'principal',
     });

     const [errors, setErrors] = useState<{ [key: string]: string }>({});

     // Determinar quais campos são necessários
     const precisaEndereco = camposFaltantes.some((campo) => campo.toLowerCase().includes('endereço') || campo.toLowerCase().includes('endereco'));

     const precisaTelefone = camposFaltantes.some((campo) => campo.toLowerCase().includes('telefone'));

     // Limpar formulário quando modal abrir
     useEffect(() => {
          if (open) {
               setErrors({});
               setEndereco({
                    cep: '',
                    logradouro: '',
                    bairro: '',
                    cidade: '',
                    uf: '',
                    numero: '',
                    complemento: '',
               });
               setTelefone({
                    ddd: '',
                    numero: '',
                    tipo: 'principal',
               });
          }
     }, [open]);

     // Validações
     const validarFormulario = (): boolean => {
          const novosErrors: { [key: string]: string } = {};

          if (precisaEndereco) {
               if (!endereco.cep.trim()) {
                    novosErrors.cep = 'CEP é obrigatório';
               } else if (!/^\d{5}-?\d{3}$/.test(endereco.cep)) {
                    novosErrors.cep = 'CEP deve ter formato 12345-678';
               }

               if (!endereco.logradouro.trim()) {
                    novosErrors.logradouro = 'Logradouro é obrigatório';
               }

               if (!endereco.bairro.trim()) {
                    novosErrors.bairro = 'Bairro é obrigatório';
               }

               if (!endereco.cidade.trim()) {
                    novosErrors.cidade = 'Cidade é obrigatória';
               }

               if (!endereco.uf.trim()) {
                    novosErrors.uf = 'Estado é obrigatório';
               }
          }

          if (precisaTelefone) {
               if (!telefone.ddd.trim()) {
                    novosErrors.ddd = 'DDD é obrigatório';
               } else if (!/^\d{2}$/.test(telefone.ddd)) {
                    novosErrors.ddd = 'DDD deve ter 2 dígitos';
               }

               if (!telefone.numero.trim()) {
                    novosErrors.numero = 'Número é obrigatório';
               } else if (!/^\d{8,9}$/.test(telefone.numero.replace(/\D/g, ''))) {
                    novosErrors.numero = 'Número deve ter 8 ou 9 dígitos';
               }
          }

          setErrors(novosErrors);
          return Object.keys(novosErrors).length === 0;
     };

     // Buscar endereço por CEP
     const buscarCep = async (cep: string) => {
          if (!/^\d{5}-?\d{3}$/.test(cep)) return;

          try {
               const cepLimpo = cep.replace(/\D/g, '');
               const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
               const data = await response.json();

               if (!data.erro) {
                    setEndereco((prev) => ({
                         ...prev,
                         logradouro: data.logradouro || '',
                         bairro: data.bairro || '',
                         cidade: data.localidade || '',
                         uf: data.uf || '',
                    }));
               }
          } catch (error) {
               console.error('Erro ao buscar CEP:', error);
          }
     };

     // Manipuladores de eventos
     const handleCepChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          const valor = event.target.value.replace(/\D/g, '');
          const cepFormatado = valor.replace(/(\d{5})(\d{3})/, '$1-$2');
          setEndereco((prev) => ({ ...prev, cep: cepFormatado }));

          if (valor.length === 8) {
               buscarCep(cepFormatado);
          }
     };

     const handleTelefoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          const valor = event.target.value.replace(/\D/g, '');
          setTelefone((prev) => ({ ...prev, numero: valor }));
     };

     const handleSalvar = async () => {
          if (!validarFormulario()) return;

          const dados: DadosCompletos = {};

          if (precisaEndereco) {
               dados.endereco = endereco;
          }

          if (precisaTelefone) {
               dados.telefone = telefone;
          }

          try {
               const resultado = await onSave(dados);
               // Se retornar uma URL, pode ser que tenha processado uma solicitação pendente
               if (resultado) {
                    // Redirecionar para pagamento
                    window.open(resultado, '_blank');
               }
               onClose();
          } catch (error) {
               console.error('Erro ao salvar dados:', error);
          }
     };

     return (
          <Dialog
               open={open}
               onClose={onClose}
               maxWidth='md'
               fullWidth
          >
               <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant='h6'>Completar Dados Cadastrais</Typography>
                    <IconButton
                         onClick={onClose}
                         size='small'
                    >
                         <CloseIcon />
                    </IconButton>
               </DialogTitle>

               <DialogContent>
                    <Alert
                         severity='info'
                         sx={{ mb: 3 }}
                    >
                         Para gerar o PIX, precisamos das seguintes informações:
                    </Alert>

                    <Box sx={{ mt: 2 }}>
                         {precisaEndereco && (
                              <Box sx={{ mb: 4 }}>
                                   <Typography
                                        variant='h6'
                                        gutterBottom
                                   >
                                        📍 Endereço Principal
                                   </Typography>

                                   <Grid
                                        container
                                        spacing={2}
                                   >
                                        <Grid
                                             item
                                             xs={12}
                                             sm={4}
                                        >
                                             <TextField
                                                  fullWidth
                                                  label='CEP'
                                                  value={endereco.cep}
                                                  onChange={handleCepChange}
                                                  error={!!errors.cep}
                                                  helperText={errors.cep}
                                                  placeholder='12345-678'
                                                  inputProps={{ maxLength: 9 }}
                                             />
                                        </Grid>

                                        <Grid
                                             item
                                             xs={12}
                                             sm={8}
                                        >
                                             <TextField
                                                  fullWidth
                                                  label='Logradouro'
                                                  value={endereco.logradouro}
                                                  onChange={(e) => setEndereco((prev) => ({ ...prev, logradouro: e.target.value }))}
                                                  error={!!errors.logradouro}
                                                  helperText={errors.logradouro}
                                             />
                                        </Grid>

                                        <Grid
                                             item
                                             xs={12}
                                             sm={3}
                                        >
                                             <TextField
                                                  fullWidth
                                                  label='Número'
                                                  value={endereco.numero}
                                                  onChange={(e) => setEndereco((prev) => ({ ...prev, numero: e.target.value }))}
                                             />
                                        </Grid>

                                        <Grid
                                             item
                                             xs={12}
                                             sm={9}
                                        >
                                             <TextField
                                                  fullWidth
                                                  label='Complemento'
                                                  value={endereco.complemento}
                                                  onChange={(e) => setEndereco((prev) => ({ ...prev, complemento: e.target.value }))}
                                                  placeholder='Apt, Casa, Bloco...'
                                             />
                                        </Grid>

                                        <Grid
                                             item
                                             xs={12}
                                             sm={6}
                                        >
                                             <TextField
                                                  fullWidth
                                                  label='Bairro'
                                                  value={endereco.bairro}
                                                  onChange={(e) => setEndereco((prev) => ({ ...prev, bairro: e.target.value }))}
                                                  error={!!errors.bairro}
                                                  helperText={errors.bairro}
                                             />
                                        </Grid>

                                        <Grid
                                             item
                                             xs={12}
                                             sm={4}
                                        >
                                             <TextField
                                                  fullWidth
                                                  label='Cidade'
                                                  value={endereco.cidade}
                                                  onChange={(e) => setEndereco((prev) => ({ ...prev, cidade: e.target.value }))}
                                                  error={!!errors.cidade}
                                                  helperText={errors.cidade}
                                             />
                                        </Grid>

                                        <Grid
                                             item
                                             xs={12}
                                             sm={2}
                                        >
                                             <FormControl
                                                  fullWidth
                                                  error={!!errors.uf}
                                             >
                                                  <InputLabel>UF</InputLabel>
                                                  <Select
                                                       value={endereco.uf}
                                                       label='UF'
                                                       onChange={(e) => setEndereco((prev) => ({ ...prev, uf: e.target.value }))}
                                                  >
                                                       {ESTADOS_BRASIL.map((estado) => (
                                                            <MenuItem
                                                                 key={estado}
                                                                 value={estado}
                                                            >
                                                                 {estado}
                                                            </MenuItem>
                                                       ))}
                                                  </Select>
                                             </FormControl>
                                        </Grid>
                                   </Grid>
                              </Box>
                         )}

                         {precisaTelefone && (
                              <Box>
                                   <Typography
                                        variant='h6'
                                        gutterBottom
                                   >
                                        📞 Telefone Principal
                                   </Typography>

                                   <Grid
                                        container
                                        spacing={2}
                                   >
                                        <Grid
                                             item
                                             xs={12}
                                             sm={3}
                                        >
                                             <TextField
                                                  fullWidth
                                                  label='DDD'
                                                  value={telefone.ddd}
                                                  onChange={(e) => setTelefone((prev) => ({ ...prev, ddd: e.target.value.replace(/\D/g, '') }))}
                                                  error={!!errors.ddd}
                                                  helperText={errors.ddd}
                                                  placeholder='11'
                                                  inputProps={{ maxLength: 2 }}
                                             />
                                        </Grid>

                                        <Grid
                                             item
                                             xs={12}
                                             sm={6}
                                        >
                                             <TextField
                                                  fullWidth
                                                  label='Número'
                                                  value={telefone.numero}
                                                  onChange={handleTelefoneChange}
                                                  error={!!errors.numero}
                                                  helperText={errors.numero}
                                                  placeholder='987654321'
                                                  inputProps={{ maxLength: 9 }}
                                             />
                                        </Grid>

                                        <Grid
                                             item
                                             xs={12}
                                             sm={3}
                                        >
                                             <FormControl fullWidth>
                                                  <InputLabel>Tipo</InputLabel>
                                                  <Select
                                                       value={telefone.tipo}
                                                       label='Tipo'
                                                       onChange={(e) => setTelefone((prev) => ({ ...prev, tipo: e.target.value as 'principal' | 'secundario' }))}
                                                  >
                                                       <MenuItem value='principal'>Principal</MenuItem>
                                                       <MenuItem value='secundario'>Secundário</MenuItem>
                                                  </Select>
                                             </FormControl>
                                        </Grid>
                                   </Grid>
                              </Box>
                         )}
                    </Box>
               </DialogContent>

               <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                         onClick={onClose}
                         disabled={loading}
                    >
                         Cancelar
                    </Button>
                    <Button
                         onClick={handleSalvar}
                         variant='contained'
                         disabled={loading}
                         startIcon={<SaveIcon />}
                    >
                         {loading ? 'Salvando...' : 'Salvar e Continuar'}
                    </Button>
               </DialogActions>
          </Dialog>
     );
};

export default ModalCadastroDados;
